import {
  Button,
  Col,
  Collapse,
  Form,
  List,
  Row,
  Typography,
  message,
  theme,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Icon from "@ant-design/icons";
import { useEffect, useState } from "react";
import { getCompanies } from "../../repository/data.repository";
import { Company, MAX_PAGE_SIZE } from "../../repository/data.default";
import { QueryOperation } from "../../code/search-query/search-query";
import { InputObject } from "../../controls/default";
import { retry, getProblemMessage } from "../../utils/http";
import { nameOf } from "../../utils/typescript";
import { Icons } from "../../assets/icons";
import DynamicForm from "../../controls/DynamicForm";

export interface UserCompanyAssignmentsPanelProps {
  readonly uid?: string;
  readonly open?: boolean;
  /** Loads the affiliate's current assignments (getCompanyConsultants / getCompanyCollaborators). */
  readonly loadFn: (input: any) => Promise<{ data: any[] }>;
  /** Adds an assignment (addCompanyConsultants / addCompanyCollaborators). */
  readonly addFn: (input: { userUid: string; companyUid: string }) => Promise<any>;
  /** Removes an assignment by its assignment uid (deleteCompanyConsultants / deleteCompanyCollaborators). */
  readonly removeFn: (assignmentUid: string) => Promise<any>;
}

/**
 * AFFILIATE tier — the many-companies "Company Assignments" panel (consultant / collaborator).
 * Self-contained: loads its own list, add via a company combobox, remove per row.
 */
function UserCompanyAssignmentsPanel(props: UserCompanyAssignmentsPanelProps) {
  const { token } = theme.useToken();
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const values = Form.useWatch<{ company?: InputObject }>([], form);
  const [companies, setCompanies] = useState<InputObject[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  useEffect(() => {
    if (props.open && props.uid) loadAssignments();
    else setCompanies([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.uid]);

  function loadAssignments(): void {
    if (!props.uid) return;
    setListLoading(true);
    props
      .loadFn({
        searchQuery: {
          conditions: [
            { field: "userUid", operation: QueryOperation.Equal, values: [props.uid] },
          ],
        },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      })
      .then(({ data }) => {
        setCompanies(data.map((item: any) => ({ display: item.companyName, value: item.uid })));
        setListLoading(false);
        form.resetFields();
      })
      .catch((e: any) => {
        setListLoading(false);
        messageApi.error(getProblemMessage(e, "Couldn't load company assignments."));
      });
  }

  function onAddCompany(): void {
    const company = values?.company;
    if (!props.uid || !company?.value) return;
    setButtonLoading(true);
    props
      .addFn({ userUid: props.uid, companyUid: company.value })
      .then(() => {
        loadAssignments();
        setButtonLoading(false);
      })
      .catch((e: any) => {
        setButtonLoading(false);
        messageApi.error(getProblemMessage(e));
      });
  }

  function onRemoveCompany(assignmentUid: string): void {
    setListLoading(true);
    props
      .removeFn(assignmentUid)
      .then(() => {
        setCompanies((prev) => prev.filter((c) => c.value !== assignmentUid));
        setListLoading(false);
      })
      .catch((e: any) => {
        setListLoading(false);
        messageApi.error(getProblemMessage(e));
      });
  }

  const companiesRequest = (search: string) =>
    retry(() =>
      getCompanies({
        searchQuery: {
          conditions: [
            { field: nameOf<Company>("name"), operation: QueryOperation.StartsWith, values: [search] },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({ display: dataItem.name, value: dataItem.uid, dataItem })),
    );

  return (
    <>
      {contextHolder}
      <Collapse
      accordion
      items={[
        {
          key: "assignments",
          label: `Company Assignments${companies.length ? ` (${companies.length})` : ""}`,
          children: (
            <Row className="flex flex-row flex-wrap">
              <Col className="flex flex-col">
                <DynamicForm
                  className="w-80"
                  form={form}
                  schema={{
                    groups: [
                      {
                        controls: [
                          {
                            type: "combobox",
                            label: "Assigned Companies",
                            name: "company",
                            request: companiesRequest,
                          },
                        ],
                      },
                    ],
                  }}
                />
                <List
                  className="ml-1 mr-1"
                  loading={listLoading}
                  style={{
                    backgroundColor: token.colorBgLayout,
                    borderRadius: token.borderRadius,
                  }}
                  dataSource={companies}
                  renderItem={(item) => (
                    <List.Item className="p-1 pl-2 pr-2">
                      <Col className="flex flex-auto flex-col">
                        <Typography.Text>{item.display}</Typography.Text>
                      </Col>
                      <Button
                        type={"text"}
                        loading={buttonLoading}
                        icon={<Icon className="-ml-0.5 text-xl" component={Icons.CloseIcon} />}
                        onClick={() => onRemoveCompany(item.value)}
                      />
                    </List.Item>
                  )}
                />
              </Col>
              <Col className="mt-7">
                <Button
                  type="primary"
                  disabled={!values?.company?.value}
                  onClick={onAddCompany}
                  icon={<PlusOutlined />}
                ></Button>
              </Col>
            </Row>
          ),
        },
      ]}
      />
    </>
  );
}

export default UserCompanyAssignmentsPanel;
